import React, { useEffect, useState } from "react";
import {
  getRefundsAll,
  getRefundsPending,
  getRefundsSettled,
  getRefundsInProgress,
} from "../../services/transaction";
import { useAuth } from "../../context/AuthContextProvider";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import Toggle from "../../components/Toggle";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";

const Refund = () => {
  const { globalProfile } = useAuth();
  const [activeTab, setActiveTab] = useState("all");
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isEndDateFocused, setIsEndDateFocused] = useState(false);

  const isSandbox = globalProfile === "TEST";

  useEffect(() => {
    const fetchRefunds = async () => {
      setLoading(true);
      try {
        let res;
        if (isSandbox) {
          res = await getRefundsAll(isSandbox);
        } else {
          if (activeTab === "pending") {
            res = await getRefundsPending(isSandbox);
          } else if (activeTab === "in-progress") {
            res = await getRefundsInProgress(isSandbox);
          } else if (activeTab === "settled") {
            res = await getRefundsSettled(isSandbox);
          } else {
            res = await getRefundsAll(isSandbox);
          }
        }

        const rawData = Array.isArray(res) ? res : res?.data || [];
        const dataList = Array.isArray(rawData) ? rawData : [];
        setRefunds(dataList);
      } catch (error) {
        if (error.response) {
          toast.error(error.response.data?.message || "Failed to fetch refunds");
        } else {
          toast.error("An error occurred while fetching refunds");
        }
        setRefunds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRefunds();
  }, [activeTab, globalProfile, isSandbox]);

  // Local Filter logic for Search, Date Range and Tab Status (in sandbox)
  const filteredRefunds = refunds.filter((info) => {
    if (isSandbox && activeTab !== "all") {
      const status = (info.refundStatus || "").toLowerCase();
      if (activeTab === "pending" && status !== "pending") {
        return false;
      }
      if (
        activeTab === "in-progress" &&
        status !== "in progress" &&
        status !== "in-progress"
      ) {
        return false;
      }
      if (
        activeTab === "settled" &&
        status !== "settled" &&
        status !== "approved"
      ) {
        return false;
      }
    }

    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (info.refundId || "").toString().toLowerCase().includes(term) ||
      (info.transactionId || "").toString().toLowerCase().includes(term) ||
      (info.customerName || "").toLowerCase().includes(term) ||
      (info.customerEmail || "").toLowerCase().includes(term) ||
      (info.refundReason || "").toLowerCase().includes(term) ||
      (info.refundType || "").toLowerCase().includes(term) ||
      (info.transactionScheme || "").toLowerCase().includes(term) ||
      (info.merchantName || "").toLowerCase().includes(term);

    let matchesDate = true;
    if (startDate && endDate) {
      const refDateStr =
        info.requestDate || info.refundDate || info.transactionDate;
      if (refDateStr) {
        const refDate = new Date(refDateStr);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        matchesDate = refDate >= start && refDate <= end;
      }
    }

    return matchesSearch && matchesDate;
  });

  const handleDownload = () => {
    if (filteredRefunds.length === 0) {
      toast.error("No refunds to export");
      return;
    }

    const rows = filteredRefunds.map((info) => ({
      "Refund ID": info.refundId ?? "N/A",
      "Transaction ID": info.transactionId ?? "N/A",
      "Merchant ID": info.merchantId ?? "N/A",
      "Merchant Name": info.merchantName ?? "N/A",
      "Customer Name": info.customerName || "N/A",
      "Customer Email": info.customerEmail || "N/A",
      "Refund Amount": info.refundAmount ?? 0,
      "Transaction Amount": info.transactionAmount ?? 0,
      Currency: info.transactionCurrency || "N/A",
      Type: info.refundType || "N/A",
      Reason: info.refundReason || "N/A",
      Scheme: info.transactionScheme || "N/A",
      "Approval Code": info.approvalCode || "N/A",
      "Processed By": info.processedBy || "N/A",
      Status: info.refundStatus || "N/A",
      "Request Date": info.requestDate || "N/A",
      "Approved Date": info.approvedDate || "N/A",
      "Transaction Date": info.transactionDate || "N/A",
      "Refund Date": info.refundDate || "N/A",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Refunds");
    XLSX.writeFile(workbook, "Refunds_Report.xlsx");
    toast.success("Refunds report exported successfully");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const refundAmountBodyTemplate = (rowData) => {
    const val = rowData.refundAmount ?? 0;
    return (
      <span className="font-bold">
        {typeof val === "number" ? val.toFixed(2) : val}
      </span>
    );
  };

  const tranAmountBodyTemplate = (rowData) => {
    const val = rowData.transactionAmount ?? 0;
    return (
      <span className="font-bold">
        {typeof val === "number" ? val.toFixed(2) : val}
      </span>
    );
  };

  const schemeBodyTemplate = (rowData) => (
    <span className="text-text-orange font-bold bg-text-orange/10 px-3 py-1.5 rounded-lg text-[10px]">
      {rowData.transactionScheme || "N/A"}
    </span>
  );

  const statusBodyTemplate = (rowData) => {
    const status = rowData.refundStatus || "N/A";
    const lower = status.toLowerCase();
    const isSuccess = lower === "settled" || lower === "approved";
    const isWarn = lower === "pending" || lower === "in progress";
    const badgeColor = isSuccess
      ? "bg-green-100 text-green-800"
      : isWarn
      ? "bg-yellow-100 text-yellow-800"
      : "bg-red-100 text-red-800";

    return (
      <span
        className={`font-semibold px-2.5 py-1 rounded-full text-[10px] capitalize ${badgeColor}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-evenly">
        <h1 className="text-navy-blue text-2xl font-bold">
          Transactions - Refund
        </h1>
        <div className="relative">
          <input
            type="text"
            placeholder="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[402px] h-[40px] rounded-[10px] px-2 border border-gray-300 focus:outline-none"
          />
          <span>
            <svg
              className="absolute right-8 top-3"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g opacity="0.5">
                <path
                  d="M7.22235 13.4445C10.6589 13.4445 13.4447 10.6587 13.4447 7.22223C13.4447 3.78579 10.6589 1 7.22235 1C3.78584 1 1 3.78579 1 7.22223C1 10.6587 3.78584 13.4445 7.22235 13.4445Z"
                  stroke="#030229"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.0001 15.0005L11.6167 11.6172"
                  stroke="#030229"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            </svg>
          </span>
        </div>

        {/* Start Date Input */}
        <div className="relative">
          <input
            type="text"
            placeholder="Start date"
            value={startDate}
            className="focus:outline-none w-[130px] h-[40px] px-3 rounded-[5px] border border-gray-300 text-sm placeholder:text-gray-400 placeholder:font-semibold"
            onChange={(e) => setStartDate(e.target.value)}
            onFocus={(e) => {
              e.target.type = "date";
              setIsFocused(true);
            }}
            onBlur={(e) => {
              e.target.type = "text";
              setIsFocused(false);
            }}
          />
          {!isFocused && (
            <span>
              <svg
                className="absolute right-4 top-[18px] pointer-events-none"
                width="9"
                height="6"
                viewBox="0 0 9 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  opacity="0.5"
                  d="M0.5 1L4.5 5L8.5 1"
                  stroke="black"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>

        {/* End Date Input */}
        <div className="relative">
          <input
            type="text"
            placeholder="End date"
            value={endDate}
            className="focus:outline-none w-[130px] h-[40px] px-3 rounded-[5px] border border-gray-300 text-sm placeholder:text-gray-400 placeholder:font-semibold"
            onChange={(e) => setEndDate(e.target.value)}
            onFocus={(e) => {
              e.target.type = "date";
              setIsEndDateFocused(true);
            }}
            onBlur={(e) => {
              e.target.type = "text";
              setIsEndDateFocused(false);
            }}
          />
          {!isEndDateFocused && (
            <span>
              <svg
                className="absolute right-4 top-[18px] pointer-events-none"
                width="9"
                height="6"
                viewBox="0 0 9 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  opacity="0.5"
                  d="M0.5 1L4.5 5L8.5 1"
                  stroke="black"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
        </div>

        <div className="relative">
          <button
            onClick={handleDownload}
            className="text-white bg-text-orange w-[165px] h-[42px] rounded-[10px]"
          >
            Export
          </button>
          <span>
            <svg
              className="absolute left-10 top-[18px] pointer-events-none"
              width="10"
              height="10"
              viewBox="0 0 10 10"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_22_2583)">
                <path
                  d="M6.91242 3.75H6.24992V1.66667C6.24992 1.4375 6.06242 1.25 5.83325 1.25H4.16659C3.93742 1.25 3.74992 1.4375 3.74992 1.66667V3.75H3.08742C2.71659 3.75 2.52909 4.2 2.79159 4.4625L4.70409 6.375C4.86659 6.5375 5.12909 6.5375 5.29159 6.375L7.20409 4.4625C7.46659 4.2 7.28325 3.75 6.91242 3.75ZM2.08325 7.91667C2.08325 8.14583 2.27075 8.33333 2.49992 8.33333H7.49992C7.72909 8.33333 7.91659 8.14583 7.91659 7.91667C7.91659 7.6875 7.72909 7.5 7.49992 7.5H2.49992C2.27075 7.5 2.08325 7.6875 2.08325 7.91667Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_22_2583">
                  <rect width="10" height="10" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </span>
        </div>

        <div className="flex items-center">
          <Toggle />
        </div>
      </div>

      <div className="bg-white rounded-[12px] shadow-sm mt-4 ml-11 px-12 py-5 min-h-[60vh]">
        {/* Tabs Header */}
        <div className="flex border-b border-gray-100 mb-6">
          <button
            onClick={() => setActiveTab("all")}
            className={`py-4 px-6 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "all"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            All Refunds
          </button>
          <button
            onClick={() => setActiveTab("pending")}
            className={`py-4 px-6 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "pending"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveTab("in-progress")}
            className={`py-4 px-6 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "in-progress"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            In Progress
          </button>
          <button
            onClick={() => setActiveTab("settled")}
            className={`py-4 px-6 font-semibold text-sm transition-all border-b-2 ${
              activeTab === "settled"
                ? "border-orange-500 text-orange-500"
                : "border-transparent text-gray-400 hover:text-gray-600"
            }`}
          >
            Settled
          </button>
        </div>

        <div className="card overflow-x-auto w-full">
          <DataTable
            value={filteredRefunds}
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 25, 50, 100]}
            emptyMessage="No refunds matching the current selection or filters."
            tableStyle={{ minWidth: "150rem" }}
            stripedRows
            responsiveLayout="scroll"
            className="p-datatable-sm"
          >
            <Column field="refundId" header="Refund ID" sortable />
            <Column field="transactionId" header="Transaction ID" sortable />
            <Column field="merchantId" header="Merchant ID" sortable />
            <Column field="merchantName" header="Merchant Name" sortable />
            <Column field="customerName" header="Customer Name" sortable />
            <Column field="customerEmail" header="Customer Email" sortable />
            <Column
              field="refundAmount"
              header="Refund Amount"
              body={refundAmountBodyTemplate}
              sortable
            />
            <Column
              field="transactionAmount"
              header="Transaction Amount"
              body={tranAmountBodyTemplate}
              sortable
            />
            <Column
              field="transactionCurrency"
              header="Currency"
              sortable
            />
            <Column field="refundType" header="Type" sortable />
            <Column field="refundReason" header="Reason" sortable />
            <Column
              field="transactionScheme"
              header="Scheme"
              body={schemeBodyTemplate}
              sortable
            />
            <Column field="approvalCode" header="Approval Code" sortable />
            <Column field="processedBy" header="Processed By" sortable />
            <Column
              field="refundStatus"
              header="Status"
              body={statusBodyTemplate}
              sortable
            />
            <Column
              field="requestDate"
              header="Request Date"
              body={(rowData) => formatDate(rowData.requestDate)}
              sortable
            />
            <Column
              field="approvedDate"
              header="Approved Date"
              body={(rowData) => formatDate(rowData.approvedDate)}
              sortable
            />
            <Column
              field="transactionDate"
              header="Transaction Date"
              body={(rowData) => formatDate(rowData.transactionDate)}
              sortable
            />
            <Column
              field="refundDate"
              header="Refund Date"
              body={(rowData) => formatDate(rowData.refundDate)}
              sortable
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};

export default Refund;
